import { useState, useEffect } from 'react';
import { Ticket, Plus, Trash2, Copy, Check, Calendar, Users, Percent, DollarSign, Package, Tag } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DiscountCodeManager() {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [packages, setPackages] = useState({});

  // Form state
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(10);
  const [usageLimit, setUsageLimit] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [applicablePackages, setApplicablePackages] = useState([]);
  const [customCode, setCustomCode] = useState('');

  useEffect(() => {
    fetchDiscountCodes();
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/payment/packages`);
      setPackages(response.data.packages || {});
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      setLoading(true);
      const adminPassword = localStorage.getItem('admin_password') || '123';
      const response = await axios.get(`${API}/admin/discount-codes`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setDiscountCodes(response.data);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
      setDiscountCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const createDiscountCode = async () => {
    try {
      setLoading(true);
      const adminPassword = localStorage.getItem('admin_password') || '123';

      const payload = {
        discount_type: discountType,
        discount_value: discountValue,
        applicable_packages: applicablePackages,
        usage_limit: usageLimit || null,
        expires_in_days: expiresInDays || null,
        custom_code: customCode || ''
      };

      const response = await axios.post(
        `${API}/admin/discount-codes/create`,
        payload,
        { headers: { 'X-Admin-Password': adminPassword } }
      );

      alert(`Indirim kodu olusturuldu: ${response.data.code}`);
      setShowCreateModal(false);
      fetchDiscountCodes();
      resetForm();
    } catch (error) {
      console.error('Error creating discount code:', error);
      alert('Indirim kodu olusturulurken hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDiscountType('percentage');
    setDiscountValue(10);
    setUsageLimit('');
    setExpiresInDays('');
    setApplicablePackages([]);
    setCustomCode('');
  };

  const deleteDiscountCode = async (codeId) => {
    if (!window.confirm('Bu indirim kodunu silmek istediginizden emin misiniz?')) return;
    try {
      const adminPassword = localStorage.getItem('admin_password') || '123';
      await axios.delete(`${API}/admin/discount-codes/${codeId}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      alert('Indirim kodu silindi');
      fetchDiscountCodes();
    } catch (error) {
      console.error('Error deleting discount code:', error);
      alert('Silme hatasi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const togglePackage = (pkgId) => {
    setApplicablePackages(prev =>
      prev.includes(pkgId) ? prev.filter(p => p !== pkgId) : [...prev, pkgId]
    );
  };

  const getDiscountLabel = (code) => {
    const type = code.discount_type || 'percentage';
    const value = code.discount_value ?? code.discount_percentage ?? 0;
    if (type === 'fixed') return `${value} SEK`;
    return `%${value}`;
  };

  const getPackageNames = (pkgIds) => {
    if (!pkgIds || pkgIds.length === 0) return 'Tum paketler';
    return pkgIds.map(id => packages[id]?.name || id).join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-900" data-testid="discount-codes-title">Indirim Kodlari</h2>
          <p className="text-gray-600">Musterilerinize ozel indirim kodlari olusturun</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-discount-btn"
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Yeni Kod Olustur
        </button>
      </div>

      {/* Discount Codes List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : discountCodes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Henuz indirim kodu yok</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {discountCodes.map((code) => {
            const isExpired = code.expires_at && new Date(code.expires_at) < new Date();
            const isLimitReached = code.usage_limit && code.used_count >= code.usage_limit;
            const dtype = code.discount_type || 'percentage';

            return (
              <div
                key={code.id}
                data-testid={`discount-card-${code.code}`}
                className={`bg-white rounded-xl p-6 shadow-lg border-2 ${
                  !code.is_active || isExpired || isLimitReached
                    ? 'border-gray-300 opacity-60'
                    : 'border-green-500'
                }`}
              >
                {/* Code with Copy */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-green-600" />
                    <span className="text-2xl font-black text-gray-900 font-mono" data-testid={`discount-code-text-${code.code}`}>
                      {code.code}
                    </span>
                  </div>
                  <button onClick={() => copyToClipboard(code.code)} className="p-2 hover:bg-gray-100 rounded-lg transition-all">
                    {copiedCode === code.code ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>

                {/* Discount Badge */}
                <div className="mb-4">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg text-white ${
                    dtype === 'fixed'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600'
                      : 'bg-gradient-to-r from-yellow-400 to-orange-500'
                  }`}>
                    {dtype === 'fixed' ? <DollarSign className="w-4 h-4" /> : <Percent className="w-4 h-4" />}
                    {getDiscountLabel(code)} Indirim
                  </div>
                </div>

                {/* Applicable Packages */}
                {code.applicable_packages && code.applicable_packages.length > 0 && (
                  <div className="mb-3 flex items-start gap-2">
                    <Package className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {code.applicable_packages.map(pkgId => (
                        <span key={pkgId} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                          {packages[pkgId]?.name || pkgId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Kullanim:</span>
                    <span className="font-bold text-gray-900">
                      {code.used_count} / {code.usage_limit || 'Sinirsiz'}
                    </span>
                  </div>
                  {code.expires_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Son Kullanma:</span>
                      <span className={`font-bold ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                        {new Date(code.expires_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Olusturulma:</span>
                    <span className="text-gray-900">
                      {new Date(code.created_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mb-4">
                  {isExpired ? (
                    <span className="inline-block px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold">Suresi Dolmus</span>
                  ) : isLimitReached ? (
                    <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-bold">Limit Doldu</span>
                  ) : code.is_active ? (
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-bold">Aktif</span>
                  ) : (
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full font-bold">Pasif</span>
                  )}
                </div>

                {/* Delete */}
                <button
                  onClick={() => deleteDiscountCode(code.id)}
                  data-testid={`delete-discount-${code.code}`}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-bold transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                  Sil
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black text-gray-900 mb-6" data-testid="create-discount-modal-title">Yeni Indirim Kodu</h3>

            <div className="space-y-5">
              {/* Custom Code (Optional) */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Ozel Kod (Opsiyonel)
                </label>
                <input
                  type="text"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                  placeholder="Bos birakilirsa otomatik olusturulur"
                  data-testid="custom-code-input"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:outline-none font-mono uppercase"
                  maxLength={20}
                />
              </div>

              {/* Discount Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Indirim Turu</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => { setDiscountType('percentage'); setDiscountValue(10); }}
                    data-testid="discount-type-percentage"
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold border-2 transition-all ${
                      discountType === 'percentage'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <Percent className="w-5 h-5" />
                    Yuzde
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDiscountType('fixed'); setDiscountValue(50); }}
                    data-testid="discount-type-fixed"
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold border-2 transition-all ${
                      discountType === 'fixed'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <DollarSign className="w-5 h-5" />
                    Sabit Tutar (SEK)
                  </button>
                </div>
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {discountType === 'percentage' ? 'Indirim Yuzdesi (1-100)' : 'Indirim Tutari (SEK)'}
                </label>
                {discountType === 'percentage' ? (
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseInt(e.target.value))}
                      data-testid="discount-value-slider"
                      className="flex-1"
                    />
                    <span className="text-3xl font-black text-green-600 w-20 text-right" data-testid="discount-value-display">
                      {discountValue}%
                    </span>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      data-testid="discount-value-input"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none pr-16"
                      min="1"
                      step="1"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">SEK</span>
                  </div>
                )}
              </div>

              {/* Applicable Packages */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Package className="w-4 h-4 inline mr-1" />
                  Gecerli Paketler
                </label>
                <p className="text-xs text-gray-500 mb-2">Hicbiri secilmezse tum paketlerde gecerli olur</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(packages).map(([pkgId, pkg]) => (
                    <button
                      key={pkgId}
                      type="button"
                      onClick={() => togglePackage(pkgId)}
                      data-testid={`package-select-${pkgId}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                        applicablePackages.includes(pkgId)
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {applicablePackages.includes(pkgId) && <Check className="w-4 h-4" />}
                      <span>{pkg.name}</span>
                      <span className="text-xs opacity-60">{pkg.amount || pkg.price} SEK</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Usage Limit */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Kullanim Limiti (Opsiyonel)
                </label>
                <input
                  type="number"
                  value={usageLimit}
                  onChange={(e) => setUsageLimit(e.target.value)}
                  placeholder="Sinirsiz kullanim icin bos birakin"
                  data-testid="usage-limit-input"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:outline-none"
                  min="1"
                />
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Gecerlilik Suresi (Gun) (Opsiyonel)
                </label>
                <input
                  type="number"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  placeholder="Suresiz icin bos birakin"
                  data-testid="expires-days-input"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-500 focus:outline-none"
                  min="1"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm font-bold text-gray-700 mb-1">Onizleme:</p>
              <p className="text-lg font-black text-gray-900">
                {customCode || 'XXXXXXXX'} - {discountType === 'fixed' ? `${discountValue} SEK` : `%${discountValue}`} indirim
              </p>
              <p className="text-xs text-gray-500">
                {getPackageNames(applicablePackages)} icin gecerli
                {usageLimit ? ` | Maks ${usageLimit} kullanim` : ''}
                {expiresInDays ? ` | ${expiresInDays} gun gecerli` : ''}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                data-testid="cancel-create-btn"
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-bold transition-all"
              >
                Iptal
              </button>
              <button
                onClick={createDiscountCode}
                disabled={loading || discountValue <= 0}
                data-testid="confirm-create-btn"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Olusturuluyor...' : 'Olustur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
